/**
 * The fan page's cart is a Map keyed by menu item id, or `<id>:<add-on names>`
 * for a line with add-ons. These helpers turn it into display lines and find
 * every line of one item.
 *
 * `buildOrderLines` must never throw. It runs right after Stripe confirms a
 * payment; an exception there used to surface as a payment error, abandon
 * the (paid) order and drop the fan's pointer to it — all because a carted
 * item had been switched off mid-match and was no longer in the menu list.
 * The server has the real lines (create-payment-intent writes order_items),
 * so a placeholder here is replaced by get_order_items within a second.
 */
export interface CartAddon {
  name: string;
  price: number;
}

export interface CartEntryLike {
  qty: number;
  addons?: CartAddon[];
}

export interface MenuItemLike {
  name: string;
  size?: string | null;
  price: number;
  vat_rate?: number | null;
}

export interface OrderLine {
  menu_item_id: string;
  item_name: string;
  item_size: string;
  item_price: number;
  quantity: number;
  selected_addons: CartAddon[];
  item_vat_rate: number;
}

/** The menu item id a cart key refers to. */
export function cartItemId(key: string): string {
  return key.split(':')[0];
}

/** Every cart key for an item: the plain one and each add-on variant. */
export function cartKeysForItem(cart: ReadonlyMap<string, unknown>, itemId: string): string[] {
  return [...cart.keys()].filter((k) => k === itemId || k.startsWith(`${itemId}:`));
}

export function buildOrderLines(
  cart: ReadonlyMap<string, CartEntryLike>,
  lookup: (itemId: string) => MenuItemLike | undefined,
): OrderLine[] {
  return Array.from(cart.entries()).map(([key, entry]) => {
    const itemId = cartItemId(key);
    const item = lookup(itemId);
    return {
      menu_item_id: itemId,
      item_name: item?.name ?? '—',
      item_size: item?.size ?? '',
      item_price: item?.price ?? 0,
      quantity: entry.qty,
      selected_addons: entry.addons ?? [],
      item_vat_rate: item?.vat_rate ?? 0,
    };
  });
}
